import React from "react";
import { createRoot } from "react-dom/client";
import { BrowserRouter } from "react-router";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import App from "./App";
import configFailure from "@companion-core/shared/app/Assets/config/configFailure.json";
import { loadLanguageAsync } from "@companion-core/shared/app/I18n";
import { useAuthStore } from "@companion-core/shared/app/Store/authStore";
import { useConfigStore } from "@companion-core/shared/app/Store/configStore";
import { Bell } from "./Components/Bell";
import { FrameBusted } from "Screens/Errors/FrameBusted";
import { useColors, useFavicon, useFonts, useStyle } from "Hooks/branding";
import {
  fetchPublicResource,
  getClientToken,
  initApiClient,
  initHashPassword,
} from "@companion-core/shared/app/Utils/apiClient";
import { fetchAllResources, fetchForms } from "@companion-core/shared/app/Utils/config";
import {
  setClientVersionInterceptor,
  setXClientInterceptors,
} from "@companion-core/shared/app/Utils/interceptors";
import { setQueryClient } from "@companion-core/shared/app/Utils/queryClient";
import { version as coreVersion } from "../package.json";
import { initTracking, sendPianoId } from "Utilities/tracking";
import { initSentry } from "Utilities/sentry";
import { getLang } from "Utilities/lang";
import { hashPassword } from "Utilities/hash";
import { setMetaThemeColor } from "Utilities/meta";
import { setWebEnvironment } from "Utilities/config";
import { stringSearchToURLSearchParams } from "./Utilities/navigation";
import { isFrameBusted } from "Utilities/frameBusting";
import { sendCookieBannerId } from "Utilities/cookieBanner";
import "@companion-core/shared/app/Assets/Fonts/Montserrat-Bold.ttf";
import "@companion-core/shared/app/Assets/Fonts/Montserrat-Light.ttf";
import "@companion-core/shared/app/Assets/Fonts/Montserrat-SemiBold.ttf";
import "Assets/Styles/scss/index.scss";

// Set App Environment
setWebEnvironment();
setXClientInterceptors();
setClientVersionInterceptor({ coreVersion, platform: "web" });

/**
 * Quick fix to force authenticated state to false when query parameters contain "code".
 * We cannot check if the companion have the SSO feature enabled because the config is null here.
 * /!\ There may be a side effect for non SSO companion if query parameters contain "code" /!\
 *
 * @function checkQueryParamsToHandleAuthState
 * @returns {void}
 */
const checkQueryParamsToHandleAuthState = (): void => {
  if (
    window.location.search &&
    stringSearchToURLSearchParams(window.location.search).get("code") !== null
  ) {
    useAuthStore.getState().setAuthenticated(false);
  }
};

export const AssetProvider = ({ children }: { children: React.ReactNode }) => {
  const { branding } = useConfigStore((state) => ({
    branding: state.branding,
  }));
  const fonts = branding?.theme?.["fonts"] ?? undefined;
  const colors = branding?.theme?.["colors"] ?? undefined;
  const styles = branding?.theme?.["styles"] ?? undefined;
  const assets = branding?.theme?.["assets"] ?? undefined;

  useFonts(fonts);
  useColors({ colors });
  useStyle({ styles });
  useFavicon({ assets });

  return <>{children}</>;
};

const migrateHashUrl = () => {
  const { search, hash } = window.location;
  if (hash.startsWith("#/")) {
    const newUrl = hash.slice(1) + search;
    window.history.replaceState({}, "", newUrl);
  }
};

migrateHashUrl();

const load = async (): Promise<boolean> => {
  // Init app
  checkQueryParamsToHandleAuthState();
  initApiClient();

  // Load public config
  const pubConfig = await fetchPublicResource(
    import.meta.env.VITE_WEB_LOCAL_HOSTNAME ?? window.location.hostname,
    "web"
  );
  if (pubConfig.success) {
    useConfigStore.getInitialState().setPublicConfig(pubConfig.data);
    initApiClient(pubConfig.data?.data?.api_url);
  } else {
    useConfigStore.getState().setConfig(configFailure);
    useConfigStore.getState().setFailure(true);
    await loadLanguageAsync(getLang(), { local: true });
    return false;
  }

  await fetchForms(pubConfig?.data?.data?.available_forms, "web");
  sendCookieBannerId();
  sendPianoId();
  await getClientToken();
  // Load config and branding resources and loadI18n
  await fetchAllResources();
  await loadLanguageAsync(getLang());
  initHashPassword(hashPassword);
  initTracking();
  initSentry();
  setMetaThemeColor("default");
  return true;
};

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
    },
  },
});

setQueryClient(queryClient);

function start() {
  const root = createRoot(document.getElementById("root") as HTMLElement);
  root.render(
    <React.StrictMode>
      {isFrameBusted() ? (
        <FrameBusted />
      ) : (
        <QueryClientProvider client={queryClient}>
          <AssetProvider>
            <BrowserRouter>
              <Bell />
              <App />
            </BrowserRouter>
          </AssetProvider>
        </QueryClientProvider>
      )}
    </React.StrictMode>
  );
}

load().then(start);
